import React, { useMemo } from "react";
import { Form, FormRenderProps, FormSpy } from "react-final-form";
import cn from "classnames";
import { FORM_ERROR } from "final-form";
import UserTermsLabel from "../texts/UserTermsLabel";
import ContinueButton from "../controls/ContinueButton";
import InfoBox from "../boxes/InfoBox";
import DigiAuthModal from "../modals/DigiAuthModal";
import PhoneNumberFields from "../form-fields/PhoneNumberFields";
import ResetPasswordLink from "./ResetPasswordLink";
import SelectField from "../form-fields/SelectField";
import SubmitError from "../form-fields/SubmitError";
import SwitchField from "../form-fields/SwitchField";
import TextField from "../form-fields/TextField";
import userApi from "../../api/user";
import { AuthProvider, DigiAuthUser, UserRole } from "../../types/user";
import { SignUpFormProps } from "./types";
import { getSavedDigiAuthValues, navigateToNextStep, saveDigiAuthValues } from "./utils";
import { getLocalLoan } from "../../utils/loan";
import { getCountryOptions } from "../../data/countries";
import { t } from "../../i18n";
import { useDigiAuth } from "../../hooks/digiAuth";
import { trackGTM, generateDataLayerEventObject, convertToDataLayerName } from "../../utils/trackGTM";
import { trackGAEventforPHD, generateRegisterByIdEventObject } from "../../utils/trackGAEventforPHD";

const initialPhoneNumber = (provider: AuthProvider, isSameRole: boolean, savedNumber?: string): string => {
  if (provider === "smartid") return "";
  return (isSameRole && savedNumber) ? savedNumber : "";
}

const getInitialValues = (
  provider: AuthProvider, role: UserRole = "borrower", signIn: boolean = false
): DigiAuthUser => {
  const savedValues = getSavedDigiAuthValues();
  const isSameRole = savedValues?.role === role;
  return {
    countryCode: (isSameRole && savedValues?.countryCode) ? savedValues.countryCode : "EE",
    idCode: (isSameRole && savedValues?.idCode) ? savedValues.idCode : "",
    phoneNumber: initialPhoneNumber(provider, isSameRole, savedValues?.phoneNumber),
    provider,
    role,
    signIn,
    termsOfUse: false,
  }
}

const DigiAuthForm = ({ isUserExists, provider, signIn, userRole, withAutoFocus }: SignUpFormProps): JSX.Element => {
  const [authState, startDigiAuth, stopDigiAuth] = useDigiAuth();
  const localLoan = getLocalLoan();

  const initialValues = useMemo<DigiAuthUser>(() => getInitialValues(provider, userRole, signIn), [provider]);
  const secondaryColor = userRole === "investor";

  const onSubmit = async (user: DigiAuthUser) => {
    if(provider === "idcard") {
      return await userApi.idCardAuth(user)
        .then(() => {
          if (userRole === "borrower" && !signIn) {
            trackGTM({
              ...generateDataLayerEventObject('create_account'),
              item_name: convertToDataLayerName(localLoan?.purpose ?? null),
            });
          }
          if (!signIn) {
            trackGAEventforPHD(generateRegisterByIdEventObject(user));
          }
          navigateToNextStep();
        })
        .catch((errors: FormErrors) => {
          return errors.message
            ? ({ [FORM_ERROR]: [t("auth.errors.id_card", { message: errors.message as string })] })
            : errors;
        });
    }
    else return startDigiAuth(user);
  }

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={(renderProps: FormRenderProps<DigiAuthUser>): JSX.Element => {
        const { handleSubmit, submitError } = renderProps
        const textFieldClass = cn("Form-field", { "Form-field--280": !isUserExists });
        return (
          <div className="mz-new-user-fields u-max-w-580">
            <FormSpy onChange={(spyProps) => saveDigiAuthValues(spyProps.values as DigiAuthUser)}/>
            <DigiAuthModal
              i18nTitle={`auth.${provider}.modal.${signIn ? "sign_in" : "create_account"}`}
              onClose={stopDigiAuth}
              onSuccess={navigateToNextStep}
              provider={provider}
              state={authState}
            />
            <form id="form_digi_auth" onSubmit={handleSubmit}>
              {provider !== "idcard" && (
                <>
                  <TextField
                    autoFocus={withAutoFocus}
                    className={textFieldClass}
                    i18key="attributes.personal_code"
                    id="field_id_code"
                    name="idCode"
                  />
                  <InfoBox i18nKey="registration.tips.personal_code"/>
                </>
              )}
              {provider === "mobileid" && (
                <PhoneNumberFields
                  countryCodeFieldName="countryCode"
                  optionType={userRole === "investor" || signIn ? "investorDigiAuth" : "borrowerDigiAuth"}
                />
              )}
              {provider === "smartid" && (
                <SelectField
                  className={textFieldClass}
                  i18nKey="attributes.country_id"
                  id="field_country_code"
                  name="countryCode"
                  options={getCountryOptions(["EE"], "smartid")}
                />
              )}
              <SubmitError className="u-mt-40" errors={submitError}/>
              {signIn && provider !== "idcard" && (
                <SwitchField
                  className="u-mt-md"
                  i18nKey="activerecord.attributes.user.remember_me"
                  id="swtch_remember_me"
                  name="rememberMe"
                />
              )}
              {!signIn && (
                <SwitchField className="u-mt-md" id="swtch_terms_of_use" name="termsOfUse">
                  <UserTermsLabel role={userRole}/>
                </SwitchField>
              )}
              {signIn && provider !== "idcard" && <div className="u-mt-lg"><ResetPasswordLink/></div>}
              <ContinueButton
                className={`Form-submit ${signIn ? "u-min-w-140" : "u-min-w-208"}`}
                disabled={authState.status !== "idle"}
                i18nKey={`controls.buttons.${signIn ? "sign_in" : "continue"}`}
                secondary={secondaryColor}
              />
            </form>
          </div>
        )
      }}
    />
  )
}

export default DigiAuthForm;
